import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import MatchScoreBar from './MatchScoreBar';
import MatchScoreDetailsModal from './MatchScoreDetailsModal';
import { useAuth } from '../contexts/AuthContext';
import { jobsAPI } from '../services/api';

export interface JobMatchBarProps {
  jobId: number;
  initialScore: number | null;
  initialBracket?: string;
  size?: 'sm' | 'md' | 'lg';
  loggedOutMode?: 'hide' | 'cta';
}

const JobMatchBar: React.FC<JobMatchBarProps> = ({
  jobId,
  initialScore,
  initialBracket,
  size = 'sm',
  loggedOutMode = 'hide',
}) => {
  const { isCandidate } = useAuth();

  const [analysis, setAnalysis] = useState<any | null>(null);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [ctaOpen, setCtaOpen] = useState(false);

  if (!isCandidate) {
    if (loggedOutMode === 'hide') return null;
    return (
      <>
        <MatchScoreBar
          score={null}
          size={size}
          onClick={() => setCtaOpen(true)}
          ariaLabel="Sign in to see your match"
        />
        {ctaOpen && (
          <div onClick={() => setCtaOpen(false)} style={overlayStyle}>
            <div onClick={(e) => e.stopPropagation()} style={ctaModalStyle}>
              <h3 style={{ margin: '0 0 8px', color: '#0f172a' }}>See how well you match</h3>
              <p style={{ color: '#475569', marginTop: 0, fontSize: 14 }}>
                Sign in to get a personalised match breakdown for this role — skills, salary, experience, and more.
              </p>
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <Link
                  to={`/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`}
                  style={primaryBtnStyle}
                  onClick={() => setCtaOpen(false)}
                >
                  Sign in
                </Link>
                <Link to="/register" style={secondaryBtnStyle} onClick={() => setCtaOpen(false)}>
                  Create an account
                </Link>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  const openModal = async () => {
    if (analysis) {
      setModalOpen(true);
      return;
    }
    setLoadingAnalysis(true);
    try {
      const res = await jobsAPI.getMyMatch(jobId);
      setAnalysis(res.ai_analysis);
    } catch {
      setAnalysis(null);
    } finally {
      setLoadingAnalysis(false);
      setModalOpen(true);
    }
  };

  return (
    <>
      <MatchScoreBar
        score={initialScore}
        size={size}
        bracket={initialBracket}
        onClick={openModal}
      />
      {modalOpen && (
        <MatchScoreDetailsModal
          analysis={analysis}
          matchScore={initialScore ?? undefined}
          onClose={() => setModalOpen(false)}
        />
      )}
      {loadingAnalysis && !modalOpen && (
        <span style={{ marginLeft: 6, fontSize: 11, color: '#94a3b8' }}>…</span>
      )}
    </>
  );
};

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15, 23, 42, 0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 9999,
  padding: 16,
};

const ctaModalStyle: React.CSSProperties = {
  background: '#fff',
  borderRadius: 12,
  maxWidth: 380,
  width: '100%',
  padding: 20,
  boxShadow: '0 20px 60px rgba(15, 23, 42, 0.25)',
};

const primaryBtnStyle: React.CSSProperties = {
  background: '#0f172a',
  color: '#fff',
  padding: '8px 14px',
  borderRadius: 8,
  textDecoration: 'none',
  fontWeight: 600,
  fontSize: 14,
};

const secondaryBtnStyle: React.CSSProperties = {
  background: 'transparent',
  color: '#0f172a',
  padding: '8px 14px',
  borderRadius: 8,
  textDecoration: 'none',
  border: '1px solid #cbd5e1',
  fontSize: 14,
};

export default JobMatchBar;
